import { SECRET_LOCATION_BLURB, SECRET_LOCATION_TITLE } from '../places/placeFlags';
import { getEventCalendarDay, startOfLocalDay } from './dateUtils';
import { outboundEventHref } from '../events/eventPublicPoster';
import {
  extractEventEndTime,
  extractEventStartTime,
  formatEventOrganizer,
  formatEventPrice,
} from '../events/eventDisplayFields';

export const SITE_URL = 'https://maptobe.com';
const SITE_NAME = 'MapToBe';
const DEFAULT_PAGE_TITLE = 'Sarajevo Events & Things to Do | MapToBe';
const DEFAULT_DESCRIPTION =
  'Discover the best events in Sarajevo — concerts, festivals, culture, and things to do. MapToBe is your curated guide to what\'s on in Sarajevo today and this week.';

/** Production URL, or current origin when running on localhost (for local share testing). */
export function getSiteBaseUrl() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return window.location.origin;
    }
  }
  return SITE_URL;
}

/** @param {string} eventId */
export function eventDetailUrl(eventId) {
  return `${getSiteBaseUrl()}/events/${encodeURIComponent(eventId)}`;
}

/** @param {string} placeId */
export function placeDetailUrl(placeId) {
  return `${getSiteBaseUrl()}/places/${encodeURIComponent(placeId)}`;
}
const SARAJEVO_PLACE = {
  '@type': 'Place',
  name: 'Sarajevo',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Sarajevo',
    addressCountry: 'BA',
  },
};

/** @param {Record<string, unknown>} event */
function getEventImageUrl(event) {
  const src = event?.imageUrl || event?.imagePath || event?.imageData;
  if (typeof src !== 'string' || !src.trim()) return undefined;
  if (src.startsWith('http')) return src;
  if (src.startsWith('/')) return `${SITE_URL}${src}`;
  return src;
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function calendarYmd(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function schemaOffset(ymd) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Sarajevo',
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date(`${ymd}T12:00:00Z`));
  const name = parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT+1';
  const match = name.match(/([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return '+01:00';
  return `${match[1]}${match[2].padStart(2, '0')}:${(match[3] || '00').padStart(2, '0')}`;
}

function clock(value) {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match || Number(match[1]) > 23) return '';
  return `${pad2(match[1])}:${match[2]}`;
}

function schemaDateTime(event, timeValue, dayShift = 0) {
  const day = getEventCalendarDay(event);
  if (!day || isNaN(day.getTime())) return undefined;
  const ymd = calendarYmd(
    new Date(day.getFullYear(), day.getMonth(), day.getDate() + dayShift)
  );
  const time = clock(timeValue);
  if (!time) return ymd;
  return `${ymd}T${time}:00${schemaOffset(ymd)}`;
}

/** Days since the event calendar day. Positive means it already happened. */
export function eventAgeDays(event, now = new Date()) {
  const day = getEventCalendarDay(event);
  if (!day || isNaN(day.getTime())) return 0;
  return Math.round((startOfLocalDay(now) - startOfLocalDay(day)) / 86400000);
}

function parseOffer(price) {
  const raw = String(price || '').trim();
  if (!raw) return null;
  if (/^free$/i.test(raw)) return { price: '0', priceCurrency: 'BAM' };
  const amount = raw.match(/(\d+(?:[.,]\d+)?)/);
  if (!amount) return null;
  const currency = /€|eur/i.test(raw) ? 'EUR' : 'BAM';
  if (currency === 'BAM' && !/km|bam/i.test(raw) && !/^\d/.test(raw)) return null;
  return { price: amount[1].replace(',', '.'), priceCurrency: currency };
}

function eventPlaceSchema(event) {
  if (!event?.location) return SARAJEVO_PLACE;
  const place = {
    '@type': 'Place',
    name: event.location,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Sarajevo',
      addressCountry: 'BA',
    },
  };
  const locationId = String(event.locationId || '').trim();
  if (locationId) place.url = placeDetailUrl(locationId);
  return place;
}

/**
 * Build Schema.org Event objects from published events.
 * @param {Array<Record<string, unknown>>} events
 * @param {number} [limit=20]
 */
export function buildEventSchemaList(events, limit = 20) {
  if (!Array.isArray(events) || events.length === 0) return [];

  return events.slice(0, limit).map((event) => {
    const image = getEventImageUrl(event);
    const pageUrl = event.id ? eventDetailUrl(event.id) : `${SITE_URL}/`;
    const outbound = outboundEventHref(event);
    const past = eventAgeDays(event) > 0;
    const organizer = formatEventOrganizer(event);
    const offer = parseOffer(formatEventPrice(event));

    const item = {
      '@type': 'Event',
      '@id': `${pageUrl}#event`,
      name: event.title || 'Sarajevo event',
      description: typeof event.description === 'string'
        ? event.description.slice(0, 500)
        : undefined,
      startDate: schemaDateTime(event, extractEventStartTime(event)),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: eventPlaceSchema(event),
      organizer: organizer
        ? { '@type': 'Organization', name: organizer }
        : { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      url: pageUrl,
    };

    const startClock = clock(extractEventStartTime(event));
    const endClock = clock(extractEventEndTime(event));
    const overnight = Boolean(startClock && endClock && endClock <= startClock);
    if (endClock) item.endDate = schemaDateTime(event, endClock, overnight ? 1 : 0);
    if (!past) item.eventStatus = 'https://schema.org/EventScheduled';
    if (image) item.image = image;
    if (outbound) item.sameAs = outbound;
    if (offer && !past) {
      item.offers = {
        '@type': 'Offer',
        price: offer.price,
        priceCurrency: offer.priceCurrency,
        url: pageUrl,
      };
    }
    return item;
  });
}

/**
 * JSON-LD for a single event detail page.
 * @param {Record<string, unknown>} event
 */
export function buildSingleEventPageSchema(event) {
  const [eventSchema] = buildEventSchemaList([event], 1);
  if (!eventSchema) return null;

  const pageUrl = eventDetailUrl(event.id);
  const description = typeof event.description === 'string'
    ? event.description.slice(0, 300)
    : `Event in Sarajevo: ${event.title || 'upcoming event'}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        name: event.title || 'Sarajevo event',
        description,
        url: pageUrl,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: SARAJEVO_PLACE,
        mainEntity: { '@id': `${pageUrl}#event` },
      },
      { ...eventSchema, mainEntityOfPage: { '@id': `${pageUrl}#webpage` } },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Sarajevo Events',
            item: SITE_URL,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: event.title || 'Event',
            item: pageUrl,
          },
        ],
      },
    ],
  };
}

const DAY_TO_SCHEMA = {
  monday: 'https://schema.org/Monday',
  tuesday: 'https://schema.org/Tuesday',
  wednesday: 'https://schema.org/Wednesday',
  thursday: 'https://schema.org/Thursday',
  friday: 'https://schema.org/Friday',
  saturday: 'https://schema.org/Saturday',
  sunday: 'https://schema.org/Sunday',
};

/** Build a short meta description for a normalized place. */
export function buildPlaceMetaDescription(place) {
  const title = place?.title || 'Sarajevo place';
  const category = place?.mainCategory ? ` — ${place.mainCategory}` : '';
  const desc = (place?.shortDescription || place?.description || '').trim();
  const base = desc
    ? `${title}${category} in Sarajevo. ${desc}`
    : `${title}${category} in Sarajevo. Discover the best places on MapToBe.`;
  return base.length > 160 ? `${base.slice(0, 157)}…` : base;
}

/**
 * JSON-LD for a single place detail page (normalized place shape).
 * @param {Record<string, any>} place
 */
export function buildSinglePlacePageSchema(place) {
  if (!place || !place.id) return null;

  const pageUrl = placeDetailUrl(place.id);

  const business = {
    '@type': 'LocalBusiness',
    '@id': pageUrl,
    name: place.title || 'Sarajevo place',
    description: (place.description || place.shortDescription || '').slice(0, 500) || undefined,
    url: pageUrl,
    address: {
      '@type': 'PostalAddress',
      ...(place.contact?.address ? { streetAddress: place.contact.address } : {}),
      addressLocality: 'Sarajevo',
      addressCountry: 'BA',
    },
    mainEntityOfPage: { '@id': pageUrl },
  };

  if (place.hasCoordinates) {
    business.geo = {
      '@type': 'GeoCoordinates',
      latitude: place.lat,
      longitude: place.lng,
    };
  }
  if (place.coverImage) business.image = place.coverImage;
  if (place.contact?.phone) business.telephone = place.contact.phone;
  if (place.contact?.instagram) {
    const insta = place.contact.instagram;
    business.sameAs = /^https?:\/\//i.test(insta)
      ? insta
      : `https://www.instagram.com/${insta.replace('@', '')}/`;
  }

  if (Array.isArray(place.workTimes) && place.workTimes.length > 0) {
    const specs = place.workTimes
      .filter(
        ({ day, open, close }) =>
          DAY_TO_SCHEMA[day] && open && close && open.toLowerCase() !== 'closed'
      )
      .map(({ day, open, close }) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: DAY_TO_SCHEMA[day],
        opens: open,
        closes: close,
      }));
    if (specs.length > 0) business.openingHoursSpecification = specs;
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        name: place.title || 'Sarajevo place',
        description: buildPlaceMetaDescription(place),
        url: pageUrl,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: SARAJEVO_PLACE,
        mainEntity: { '@id': pageUrl },
      },
      business,
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Sarajevo Places',
            item: `${SITE_URL}/places`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: place.title || 'Place',
            item: pageUrl,
          },
        ],
      },
    ],
  };
}

export const SECRET_PLACE_PAGE_TITLE = `${SECRET_LOCATION_TITLE} | MapToBe`;
export const SECRET_PLACE_PAGE_DESCRIPTION = SECRET_LOCATION_BLURB;

/** Guest-safe document head for a secret place. No venue name, cover, or address. */
export function buildSecretPlacePageMeta(place) {
  return {
    title: SECRET_PLACE_PAGE_TITLE,
    description: SECRET_PLACE_PAGE_DESCRIPTION,
    canonical: place?.id ? placeDetailUrl(place.id) : undefined,
  };
}

/** Guest-safe JSON-LD. Omits LocalBusiness, geo, and the real title. */
export function buildSecretPlacePageSchema(place) {
  if (!place?.id) return null;
  const pageUrl = placeDetailUrl(place.id);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: SECRET_LOCATION_TITLE,
        description: SECRET_PLACE_PAGE_DESCRIPTION,
        url: pageUrl,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Sarajevo Places',
            item: `${SITE_URL}/places`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: SECRET_LOCATION_TITLE,
            item: pageUrl,
          },
        ],
      },
    ],
  };
}

/** Build a short meta description for an event. */
export function buildEventMetaDescription(event) {
  const title = event?.title || 'Sarajevo event';
  const location = event?.location ? ` at ${event.location}` : ' in Sarajevo';
  const desc = typeof event?.description === 'string' ? event.description.trim() : '';
  const base = desc
    ? `${title}${location}. ${desc}`
    : `${title}${location}. Discover upcoming Sarajevo events on MapToBe.`;
  return base.length > 160 ? `${base.slice(0, 157)}…` : base;
}

/**
 * Update document title and key meta tags for a route.
 * @param {{ title?: string, description?: string, canonical?: string, image?: string }} meta
 */
export function setPageMeta(meta) {
  const { title, description, canonical, image } = meta;

  if (title) document.title = title;

  const upsertMeta = (attr, key, content) => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'title', title);
  upsertMeta('name', 'robots', meta.robots);
  upsertMeta('name', 'googlebot', meta.robots);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'twitter:title', title);
  upsertMeta('property', 'twitter:description', description);
  upsertMeta('property', 'twitter:url', canonical);
  if (image) {
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'twitter:image', image);
  }

  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
  }
}

/** Restore homepage default meta after leaving an event detail page. */
export function resetPageMeta() {
  setPageMeta({
    title: DEFAULT_PAGE_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonical: `${SITE_URL}/`,
    image: `${SITE_URL}/og-image.png`,
    robots: 'index, follow',
  });
}

/**
 * ItemList + CollectionPage JSON-LD for the homepage events section.
 * @param {Array<Record<string, unknown>>} events
 * @param {number} [limit=20]
 */
export function buildEventsCollectionSchema(events, limit = 20) {
  const eventItems = buildEventSchemaList(events, limit);
  if (eventItems.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${SITE_URL}/#webpage`,
        name: 'Events in Sarajevo',
        description:
          'Upcoming concerts, festivals, culture, and things to do in Sarajevo. Curated by MapToBe.',
        url: `${SITE_URL}/`,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: SARAJEVO_PLACE,
        mainEntity: {
          '@type': 'ItemList',
          name: 'Upcoming Sarajevo events',
          numberOfItems: eventItems.length,
          itemListElement: eventItems.map((ev, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: ev.url,
            name: ev.name,
          })),
        },
      },
    ],
  };
}

/** WebSite + Organization JSON-LD for the homepage. */
export function buildWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        alternateName: ['M2B', 'MapToBe Sarajevo'],
        url: SITE_URL,
        description:
          'Sarajevo events, concerts, and things to do — your urban guide to the best experiences in Sarajevo.',
        inLanguage: 'en',
        publisher: { '@id': `${SITE_URL}/#organization` },
        about: SARAJEVO_PLACE,
      },
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo512.png`,
        },
        sameAs: ['https://www.instagram.com/maptobe.app/'],
        areaServed: SARAJEVO_PLACE,
      },
    ],
  };
}

/**
 * Inject or replace a JSON-LD script in document head.
 * @param {string} id
 * @param {object|null} data
 */
export function setJsonLdScript(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  document.getElementById('m2b-ssr-schema')?.remove();

  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}
